import { sanitizeEmailImageSource } from '@numeo/types-main-web-app'

const TEMPLATE_VARIABLE_RE = /\{\{([a-zA-Z_][a-zA-Z0-9_]*)\}\}/g

// Walk text nodes under `root` and wrap bare {{var}} occurrences in
// <span data-variable="var">{{var}}</span>. Text nodes whose ancestor chain
// already contains a [data-variable] element are skipped, so re-wrapping
// editor-serialized HTML is a no-op.
const wrapTemplateVarsInTextNodes = (doc: Document, root: Node): void => {
    const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT)
    const candidates: Text[] = []
    for (let node = walker.nextNode(); node !== null; node = walker.nextNode()) {
        const text = node as Text
        if (!text.textContent || !text.textContent.includes('{{')) continue
        let skip = false
        for (let p: Node | null = text.parentNode; p && p !== root; p = p.parentNode) {
            if (p.nodeType === 1 && (p as Element).hasAttribute('data-variable')) {
                skip = true
                break
            }
        }
        if (!skip) candidates.push(text)
    }

    for (const textNode of candidates) {
        const text = textNode.textContent || ''
        const frag = doc.createDocumentFragment()
        let lastIndex = 0
        TEMPLATE_VARIABLE_RE.lastIndex = 0
        let match: RegExpExecArray | null
        while ((match = TEMPLATE_VARIABLE_RE.exec(text)) !== null) {
            if (match.index > lastIndex) {
                frag.appendChild(doc.createTextNode(text.slice(lastIndex, match.index)))
            }
            const span = doc.createElement('span')
            span.setAttribute('data-variable', match[1])
            span.textContent = `{{${match[1]}}}`
            frag.appendChild(span)
            lastIndex = match.index + match[0].length
        }
        if (lastIndex === 0) continue // no matches
        if (lastIndex < text.length) {
            frag.appendChild(doc.createTextNode(text.slice(lastIndex)))
        }
        textNode.replaceWith(frag)
    }
}

/**
 * Wrap bare {{var}} occurrences in HTML with <span data-variable="var">.
 * Structural (DOMParser-based) — never double-wraps existing data-variable
 * spans, and safe to run on editor-serialized HTML or freshly pasted content.
 * Returns the input unchanged when DOMParser is unavailable (SSR, tests without jsdom).
 */
export const wrapTemplateVariables = (html: string): string => {
    if (!html || typeof DOMParser === 'undefined') return html
    const doc = new DOMParser().parseFromString(`<!DOCTYPE html><html><body>${html}</body></html>`, 'text/html')
    wrapTemplateVarsInTextNodes(doc, doc.body)
    return doc.body.innerHTML
}

/**
 * Back-compat alias. Older call sites load legacy `{{var}}` templates into Tiptap.
 */
export const migrateTemplateHtml = wrapTemplateVariables

/**
 * Strips Tiptap-specific HTML attributes and normalizes output for storage/sending.
 * Converts <span data-variable="var">...</span> back to {{var}} for template processing.
 */
export const serializeTemplateHtml = (html: string): string => {
    if (!html) return html
    return html.replace(/<span[^>]*data-variable="([^"]+)"[^>]*>.*?<\/span>/g, '{{$1}}')
}

/**
 * Validate and canonicalize a safe email image source. Returns an empty string
 * for active, malformed, or unsupported sources.
 */
export const sanitizeImageUrl = sanitizeEmailImageSource

/**
 * Escape a value for safe interpolation inside a double-quoted HTML attribute.
 */
export const escapeHtmlAttr = (value: string): string => value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

// Strip top margin from the first block element so text aligns with the logo.
const normalizeFirstBlockMargin = (html: string): string => {
    if (!html) return html
    return html.replace(/^(\s*)<(p|div|h[1-6]|ul|ol)\b([^>]*)>/i, (_match, prefix, tag, attrs) => {
        if (/style\s*=\s*"/i.test(attrs)) {
            return `${prefix}<${tag}${attrs.replace(/style\s*=\s*"([^"]*)"/i, 'style="margin-top:0;$1"')}>`
        }
        return `${prefix}<${tag}${attrs} style="margin-top:0;">`
    })
}

export type SignatureLogoPosition = 'left' | 'right' | 'top' | 'bottom'

interface SignatureLayoutOptions {
    position?: SignatureLogoPosition
    maxWidth?: number
}

export const SIGNATURE_LOGO_DEFAULT_MAX_WIDTH = 100
export const SIGNATURE_LOGO_MIN_MAX_WIDTH = 40
export const SIGNATURE_LOGO_MAX_MAX_WIDTH = 320

const VALID_POSITIONS: ReadonlyArray<SignatureLogoPosition> = ['left', 'right', 'top', 'bottom']

const clampMaxWidth = (raw: number | undefined): number => {
    if (raw === undefined || !Number.isFinite(raw)) return SIGNATURE_LOGO_DEFAULT_MAX_WIDTH
    const n = Math.round(raw)
    if (n < SIGNATURE_LOGO_MIN_MAX_WIDTH) return SIGNATURE_LOGO_MIN_MAX_WIDTH
    if (n > SIGNATURE_LOGO_MAX_MAX_WIDTH) return SIGNATURE_LOGO_MAX_MAX_WIDTH
    return n
}

const normalizePosition = (raw: string | null | undefined): SignatureLogoPosition => {
    return VALID_POSITIONS.includes(raw as SignatureLogoPosition) ? (raw as SignatureLogoPosition) : 'left'
}

/**
 * Build an email-safe table layout that places a signature logo image around
 * the signature text. The image source is validated and HTML-escaped. If it
 * fails validation, the text alone is returned (no <img> is emitted) and a
 * warning is logged so the silent drop is discoverable in devtools.
 *
 * Layout `left` matches the historical output byte-for-byte at the default
 * size, so legacy templates rerender unchanged.
 */
export const buildSignatureTable = (imageUrl: string, text: string, options?: SignatureLayoutOptions): string => {
    const safeUrl = sanitizeImageUrl(imageUrl)
    if (!safeUrl) {
        if (imageUrl) {
            console.warn('[tiptap] unsafe signature logo source rejected; falling back to text-only signature')
        }
        return text
    }
    const escapedUrl = escapeHtmlAttr(safeUrl)
    const normalizedText = normalizeFirstBlockMargin(text)
    const position = normalizePosition(options?.position)
    const maxWidth = clampMaxWidth(options?.maxWidth)

    const imgTag = `<img data-signature-logo="1" src="${escapedUrl}" alt="Signature" style="max-width:${maxWidth}px;max-height:${maxWidth}px;object-fit:contain;display:block;" />`
    const tableOpen = `<table data-signature-container="1" data-signature-logo-position="${position}" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;margin:0;"><tbody>`
    const tableClose = '</tbody></table>'

    if (position === 'right') {
        return `${tableOpen}<tr><td data-signature-text="1" style="vertical-align:top;padding:0 16px 0 0;">${normalizedText}</td><td data-signature-logo-cell="1" style="vertical-align:top;padding:0;line-height:0;">${imgTag}</td></tr>${tableClose}`
    }
    if (position === 'top') {
        return `${tableOpen}<tr><td data-signature-logo-cell="1" style="vertical-align:top;padding:0 0 8px 0;line-height:0;">${imgTag}</td></tr><tr><td data-signature-text="1" style="vertical-align:top;padding:0;">${normalizedText}</td></tr>${tableClose}`
    }
    if (position === 'bottom') {
        return `${tableOpen}<tr><td data-signature-text="1" style="vertical-align:top;padding:0 0 8px 0;">${normalizedText}</td></tr><tr><td data-signature-logo-cell="1" style="vertical-align:top;padding:0;line-height:0;">${imgTag}</td></tr>${tableClose}`
    }
    // 'left' (default) — preserves the historical byte-for-byte output at default size
    return `${tableOpen}<tr><td data-signature-logo-cell="1" style="vertical-align:top;padding:0 16px 0 0;line-height:0;">${imgTag}</td><td data-signature-text="1" style="vertical-align:top;padding:0;">${normalizedText}</td></tr>${tableClose}`
}

interface ParsedSignatureLayout {
    position: SignatureLogoPosition
    maxWidth: number
}

/**
 * Read the layout options encoded in a previously rendered signature table.
 * Returns the defaults when the markup predates layout-encoding (legacy
 * templates always render as `left` at 100px).
 */
export const readSignatureLayoutFromImg = (img: HTMLImageElement | null, container: Element | null): ParsedSignatureLayout => {
    const rawPosition = container?.getAttribute('data-signature-logo-position')
    const styleMaxWidth = img?.style.maxWidth
    let maxWidth = SIGNATURE_LOGO_DEFAULT_MAX_WIDTH
    if (styleMaxWidth) {
        const match = styleMaxWidth.match(/(\d+)/)
        if (match) maxWidth = clampMaxWidth(parseInt(match[1], 10))
    }
    return {
        position: normalizePosition(rawPosition),
        maxWidth,
    }
}

/**
 * Extract the src of the structured signature logo (`<img data-signature-logo>`) from
 * signature HTML, or null when there is none. Returns the canonical safe source.
 */
export const extractSignatureLogoSrc = (html: string | undefined | null): string | null => {
    if (!html) return null
    const tag = html.match(/<img\b[^>]*\bdata-signature-logo\b[^>]*>/i)?.[0]
    if (!tag) return null
    const src = tag.match(/\bsrc\s*=\s*"([^"]*)"/i)?.[1] || tag.match(/\bsrc\s*=\s*'([^']*)'/i)?.[1]
    return src ? sanitizeImageUrl(src) || null : null
}
